import { ConstantItems, getDefaultConfig } from "@constants/shared";
import * as _ from "lodash";
import { FeeType, PortfolioFee } from "@models/fees/fee.models";

export class FeesRepo {
  /** @ngInject */
  constructor(
    private $http: angular.IHttpService,
    private $q: angular.IQService,
    private config: ConstantItems
  ) {}

  getManagementFeesSummary(fromDate, toDate, currency) {
    let queryParams = {
      toDate: toDate,
      fromDate: fromDate,
      currency: currency,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary`, config);
  }

  getClientManagementFees = (clientId, feeDate, currency) => {
    let queryParams = {
      feeDate: feeDate,
      currency: currency,
    };

    let config = getDefaultConfig();

    config.params = queryParams;
    return this.$http.get(
      `${this.config.feesUrl}/summary/clients/${clientId}`,
      config
    );
  };

  getFeeTypes = (isActive: boolean = true) => {
    let queryParams = {
      isActive: isActive,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/feetypes`, config);
  };

  saveFeeType = (feeType: FeeType) => {
    return this.$http.post(`${this.config.feesUrl}/feetypes`, feeType);
  };

  editFeeType = (feeType: FeeType) => {
    return this.$http.put(`${this.config.feesUrl}/feetypes`, feeType);
  };

  getClientFees = (clientNumber: string) => {
    return this.$http.get(`${this.config.feesUrl}/clients/${clientNumber}`);
  };

  savePortfolioFees = (portfolioFee: PortfolioFee) => {
    return this.$http.post(`${this.config.feesUrl}/clients`, portfolioFee);
  };

  editClientFee = (portfolioFee: PortfolioFee) => {
    return this.$http.put(`${this.config.feesUrl}/clients`, portfolioFee);
  };

  approvePortfolioFee = (portfolioFeeId: number) => {
    return this.$http.post(`${this.config.feesUrl}/clients/${portfolioFeeId}/approve`, {});
  };

  getPortfolioFeeExclusion = (portfolioId: number) => {
    return this.$http.get(
      `${this.config.feesUrl}/portfolios/${portfolioId}/exclusions`
    );
  };

  getClientFeeExclusionsByClientNumber = (clientNumber: string) => {
    return this.$http.get(
      `${this.config.feesUrl}/clients/${clientNumber}/exclusions`
    );
  };

  savePortfolioFeeExclusion = (portfolioFeeExclusion) => {
    return this.$http.post(
      `${this.config.feesUrl}/portfolios/exclusions`,
      portfolioFeeExclusion
    );
  };

  editPortfolioFeeExclusion = (portfolioFeeExclusion) => {
    return this.$http.put(
      `${this.config.feesUrl}/portfolios/exclusions`,
      portfolioFeeExclusion
    );
  };

  getManagementFeeCashAccounts = () => {
    return this.$http.get(`${this.config.feesUrl}/cashaccounts`);
  };

  getClientFeeCashAccounts = (clientId) => {
    return this.$http.get(
      `${this.config.feesUrl}/clients/${clientId}/cashaccounts`
    );
  };

  addEditPortfolioFeeCashAccount = (accountDetails) => {
    return this.$http.post(
      `${this.config.feesUrl}/portfolios/cashaccounts`,
      accountDetails
    );
  };

  getManagementFeeParameters = (
    fromDate,
    toDate,
    status,
    currency,
    calculationType,
    threshold = 10
  ) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      status: status,
      currency: currency,
      calculationType: calculationType,
      threshold: threshold,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/parameters`,
      config
    );
  };

  saveTempRunParameters = (tempRunParameters) => {
    return this.$http.post(
      `${this.config.feesUrl}/instructions/parameters`,
      tempRunParameters
    );
  };

  getFinalRunParameters = (fromDate, toDate, calculationType, threshold = 10) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
      threshold: threshold,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/parameters/finalrun`,
      config
    );
  };

  declineFeeInstruction = (feeInstructionId) => {
    return this.$http.delete(
      `${this.config.feesUrl}/instructions/parameters/${feeInstructionId}`
    );
  };

  bulkDeclineFeeInstructions = (feeInstructionIds: number[]) => {
    return this.$http.post(
      `${this.config.feesUrl}/instructions/parameters/decline/bulk`,
      feeInstructionIds
    );
  };

  generateInstructions = (instructions, valueDate) => {
    let queryParams = {
      valueDate: valueDate,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.post(
      `${this.config.feesUrl}/instructions/generate`,
      instructions,
      config
    );
  };

  getFeeInstructionSheets = (fromDate, toDate, feeCalculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      feeCalculationType: feeCalculationType,
    };
    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/instructions/sheets`, config);
  };

  downloadFeeInstruction = (sheetId) => {
    return this.$http.get(
      `${this.config.feesUrl}/instructions/sheets/${sheetId}/download`
    );
  };

  getMidRate = (calculationDate) => {
    let queryParams = {
      calculationDate: calculationDate,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/instructions/rates`, config);
  };

  generateAdhocFeeInstructionSheet = (
    fromDate,
    toDate,
    calculationType,
    includeInstructed: boolean = false
  ) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
      includeInstructed: includeInstructed,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/generate/parameters/finalrun`,
      config
    );
  };

  generateAdhocTempRunSheet = (fromDate, toDate, calculationType) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/generate/parameters/temprun`,
      config
    );
  };

  getTempRunFees = (
    fromDate,
    toDate,
    status,
    currency,
    calculationType,
    threshold = 10,
    externalSourceParam?: string
  ) => {
    let queryParams: Record<string, unknown> = {
      fromDate: fromDate,
      toDate: toDate,
      status: status,
      currency: currency,
      calculationType: calculationType,
      threshold: threshold,
    };
    if (externalSourceParam != null) {
      queryParams.externalSourceParam = externalSourceParam;
    }

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/parameters/temprunfees`,
      config
    );
  };

  generateAdhocTempRunSheetFromTempRunFees = (
    fromDate,
    toDate,
    calculationType,
    externalSourceParam?: string
  ) => {
    let queryParams: Record<string, unknown> = {
      fromDate: fromDate,
      toDate: toDate,
      calculationType: calculationType,
    };
    if (externalSourceParam != null) {
      queryParams.externalSourceParam = externalSourceParam;
    }

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/instructions/generate/parameters/temprunfees`,
      config
    );
  };

  getManagementFeesByCustodian = (fromDate, toDate, currency, isDaily) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      currency: currency,
      isDailyCalculationPeriod: isDaily,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/custodians`,
      config
    );
  };

  getPortfolioFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/portfolios`,
      config
    );
  };

  getClientFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/clients`,
      config
    );
  };

  getFeeTypesFeesByLimit = (
    date,
    limit = 10,
    displayTop = true,
    displayCurrency,
    feeCalculationType,
    sortByNav = false
  ) => {
    let queryParams = {
      date: date,
      displayTop: displayTop,
      displayCurrency: displayCurrency,
      limit: limit,
      feeCalculationType: feeCalculationType,
      sortByNav: sortByNav,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(
      `${this.config.feesUrl}/summary/fees/feetypes`,
      config
    );
  };



  getIndividualClientsAuditExcel = (fromDate, toDate, isDaily, clientNumbers, relationshipManagerIds) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      clientNumbers: clientNumbers,
      isDailyCalculationPeriod: isDaily,
      relationshipManagerIds: relationshipManagerIds,
    };

    let config = getDefaultConfig();

    config.params = queryParams;

    return this.$http.get(`${this.config.feesUrl}/summary/fees/audit/individual`, config);
  };

  recalculateFees = (clientFees) => {
    return this.$http.post(
      `${this.config.feesUrl}/clients/recalculate`,
      JSON.stringify(clientFees)
    );
  };

  calculateFees = (clientNumber, calculationDate, isDaily) => {
    let config = getDefaultConfig();
    return this.$http.post(`${this.config.feesUrl}/clients/calculate?clientNumber=${clientNumber}&calculationDate=${calculationDate}&isDaily=${isDaily}`, config);
  };

  calculateAllDailyFees = (calculationDate) => {
    let config = getDefaultConfig();
    return this.$http.get(`${this.config.feesUrl}/calculate/daily?calculationDate=${calculationDate}`, config);
  };

  calculateAllEOMFees = (calculationDate) => {
    let config = getDefaultConfig();
    return this.$http.get(`${this.config.feesUrl}/calculate/monthly?calculationDate=${calculationDate}`, config);
  };

  // Note: comment back in if you need to recalculate daily fees for all clients for a full month from local.
  // This is commented out as it is for devs to locally recalculate daily fees for all clients for a full month.
  // calculateAllMonthsDailyFees = (calculationDate) => {
  //   let config = getDefaultConfig();
  //   return this.$http.get(`${this.config.feesUrl}/calculate/month/daily?calculationDate=${calculationDate}`, config);
  // };

  getNavFeesSummary = (fromDate, toDate, isDaily) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/navfees`, config);
  };

  getNavFeesSummaryFile = (fromDate, toDate, isDaily) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/navfees/file`, config);
  };

  getAuditExcel = (fromDate, toDate, isDaily) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/fees/audit`, config);
  };

  getClientFeesSummaryList = (fromDate, toDate, isDaily, clientNumbers?, relationshipManagerIds?) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
    };

    if (clientNumbers && clientNumbers.length > 0) {
      queryParams['clientNumbers'] = clientNumbers;
    }

    if (relationshipManagerIds && relationshipManagerIds.length > 0) {
      queryParams['relationshipManagerIds'] = relationshipManagerIds;
    }

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/navfeeclientlist`, config);
  };

  getClientFeesSummary = (fromDate, toDate, isDaily, clientNumber) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
      clientNumber: clientNumber,
    };
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/clientnavfeesSummary`, config);
  };

  getClientFeesSummaryAudit = (fromDate, toDate, isDaily, clientName?) => {
    let queryParams = {
      fromDate: fromDate,
      toDate: toDate,
      isDailyCalculationPeriod: isDaily,
    };
    if (clientName) {
      queryParams['clientName'] = clientName;
    }
    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/summary/clientfees/audit`, config);
  };

  /**
   * Portfolio Activity Summary (date-range based).
   * Response: ActivityCycleSummaryDTO
   */
  getPortfolioActivitySummary = (fromDate: string, toDate: string, accountSource: string) => {
    const config = getDefaultConfig();
    config.params = { fromDate, toDate, accountSource };
    return this.$http.get(`${this.config.feesUrl}/portfolioActivity/summary`, config);
  };

  /**
   * Portfolio Activity Details (date-range based).
   * Status filtering is done on the frontend.
   * Response: { results: ActivityCycleDetailDTO[], count: number }
   */
  getPortfolioActivityDetails = (fromDate: string, toDate: string, accountSource: string) => {
    const config = getDefaultConfig();
    config.params = { fromDate, toDate, accountSource };
    return this.$http.get(`${this.config.feesUrl}/portfolioActivity/details`, config);
  };

  /**
   * Client Activity Summary (date-range based, all account sources).
   * Response: ActivityCycleSummaryDTO
   */
  getClientActivitySummary = (fromDate: string, toDate: string) => {
    const config = getDefaultConfig();
    config.params = { fromDate, toDate };
    return this.$http.get(`${this.config.feesUrl}/clientActivity/summary`, config);
  };

  /**
   * Client Activity Details (date-range based, all account sources).
   * Response: { results: ActivityCycleClientDetailDTO[], count: number }
   */
  getClientActivityDetails = (fromDate: string, toDate: string) => {
    const config = getDefaultConfig();
    config.params = { fromDate, toDate };
    return this.$http.get(`${this.config.feesUrl}/clientActivity/details`, config);
  };
}



// WEBPACK FOOTER //
// ./src/services/fees.repo.ts